import { extractApiErrorInfo } from '@/lib/api/client'

export type ScanDuplicateMode = 'reject' | 'highlight'

export function formatScanDuplicateMessage(err: unknown): string {
  const { message, code, details } = extractApiErrorInfo(err)
  if (code === 'duplicate_in_other_box' || code === 'duplicate_in_node') {
    return formatDuplicateConflictMessage(details, message)
  }
  return message
}

/** Human-readable duplicate location: code + task + box (and pallet when known). */
export function formatDuplicateConflictMessage(
  details?: Record<string, unknown>,
  fallback?: string,
): string {
  const raw = stringDetail(details, 'raw') ?? stringDetail(details, 'canonical')
  const taskId = stringDetail(details, 'task_id')
  const dupBox = numberDetail(details, 'dup_box_num') ?? numberDetail(details, 'box_num')
  const dupPallet = numberDetail(details, 'dup_pallet_num') ?? numberDetail(details, 'pallet_num')

  const codeLabel = raw ? `Код «${raw}»` : 'Код'
  if (dupBox == null && !taskId) {
    return fallback || 'Дубликат кода'
  }

  const where: string[] = []
  if (taskId) where.push(`задании ${taskId}`)
  if (dupPallet != null) where.push(`паллете №${dupPallet}`)
  if (dupBox != null) where.push(`коробке №${dupBox}`)

  if (where.length === 0) {
    return fallback || 'Дубликат кода'
  }
  return `${codeLabel} уже в ${where.join(', ')}`
}

export function formatDuplicateLocation(
  dupBoxNum?: number,
  dupPalletNum?: number,
  taskId?: string,
  rawCode?: string,
): string | undefined {
  const msg = formatDuplicateConflictMessage(
    {
      raw: rawCode,
      task_id: taskId,
      dup_box_num: dupBoxNum,
      dup_pallet_num: dupPalletNum,
    },
    undefined,
  )
  return msg === 'Дубликат кода' ? undefined : msg
}

function stringDetail(details: Record<string, unknown> | undefined, key: string): string | undefined {
  if (!details) return undefined
  const v = details[key]
  return typeof v === 'string' && v.trim() !== '' ? v : undefined
}

function numberDetail(details: Record<string, unknown> | undefined, key: string): number | null {
  if (!details) return null
  const v = details[key]
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  return null
}
